import { useEffect, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { X, Send } from "lucide-react";
import { TID } from "@/constants/testIds";
import { streamChat, API } from "@/lib/api";
import { track } from "@/lib/analytics";
import axios from "axios";

/** Peat — the forest-owl mascot chatbot. Captures a soft lead before answering. */
const OwlMascot = ({ size = 40 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} aria-hidden="true">
    <ellipse cx="32" cy="38" rx="20" ry="20" fill="#0B4A3D" />
    <path d="M14 40 Q16 52 26 54 Q20 46 20 36 Z" fill="#00A676" opacity="0.9" />
    <path d="M50 40 Q48 52 38 54 Q44 46 44 36 Z" fill="#00A676" opacity="0.9" />
    <circle cx="25" cy="34" r="6.2" fill="#F4F4EF" />
    <circle cx="39" cy="34" r="6.2" fill="#F4F4EF" />
    <circle cx="25" cy="35" r="2.6" fill="#111" />
    <circle cx="39" cy="35" r="2.6" fill="#111" />
    <circle cx="25.9" cy="34.2" r="0.9" fill="#fff" />
    <circle cx="39.9" cy="34.2" r="0.9" fill="#fff" />
    <path d="M32 40 L29 44 L35 44 Z" fill="#A6CE39" />
    <path d="M32 15 Q26 8 22 12 Q28 14 31 20 Q34 14 42 12 Q38 8 32 15 Z" fill="#00A676" />
    <path d="M32 15 L32 22" stroke="#0B4A3D" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

// Soft lead gate — one field at a time, conversational
const GATE_STEPS = [
  { key: "name", prompt: "Before we start — what should I call you?", validate: (v) => v.trim().length >= 2, error: "A first name works — anything at least 2 letters." },
  { key: "mobile", prompt: "Nice to meet you, {name}. If our team ever needs to circle back — what's a good mobile number?", validate: (v) => /^[0-9+\-\s]{7,}$/.test(v.trim()), error: "That doesn't quite look like a phone number." },
  { key: "email", prompt: "And an email? Just so we're not typing thumbs to answer everything.", validate: (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim()), error: "Hmm, that email doesn't look right. Try again?" },
  { key: "city", prompt: "Last one — which city are you writing in from?", validate: (v) => v.trim().length >= 2, error: "A city name (at least 2 letters)." },
];

const GREETING = "Hello. I'm Peat — the resident forest-owl of Pali Hill City.\n\nIf you'd rather not jump straight into an online presentation, you're in the right nest. I'm here to answer any question about the developer (Malpani Infra), the five pillars, the plot paperwork, the amenities, or the Chapter III launch. And if you'd prefer, I can also send the details straight to your WhatsApp.";

const LEAD_KEY = "phc-chat-lead";
const SESSION_KEY = "phc-chat-session";

export default function ChatbotMascot() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [lead, setLead] = useState(() => {
    try {
      const raw = localStorage.getItem(LEAD_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const [gateStep, setGateStep] = useState(() => (localStorage.getItem(LEAD_KEY) ? -1 : 0));
  const [gateDraft, setGateDraft] = useState({});
  const [pendingUserQ, setPendingUserQ] = useState(null);
  const scrollerRef = useRef(null);
  const sessionRef = useRef(null);
  const proactiveTriggered = useRef(false);
  const { pathname } = useLocation();

  // Init session id
  useEffect(() => {
    let s = sessionStorage.getItem(SESSION_KEY);
    if (!s) {
      s = `sess-${Math.random().toString(36).slice(2)}-${Date.now()}`;
      sessionStorage.setItem(SESSION_KEY, s);
    }
    sessionRef.current = s;
  }, []);

  useEffect(() => {
    if (msgs.length === 0) {
      const first = { role: "assistant", text: GREETING };
      if (gateStep >= 0) {
        setMsgs([first, { role: "assistant", text: GATE_STEPS[gateStep].prompt }]);
      } else {
        setMsgs([first]);
      }
    }
  }, [gateStep, msgs.length]);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [msgs, open]);

  // Proactive open after 30s on high-intent pages
  useEffect(() => {
    if (proactiveTriggered.current) return;
    const highIntent =
      pathname === "/" ||
      pathname.startsWith("/event") ||
      pathname.startsWith("/pillars") ||
      pathname.startsWith("/eoi");
    if (!highIntent) return;
    const t = setTimeout(() => {
      if (!proactiveTriggered.current) {
        proactiveTriggered.current = true;
        setOpen(true);
      }
    }, 30_000);
    return () => clearTimeout(t);
  }, [pathname]);

  const persistLead = useCallback(async (finalDraft) => {
    setLead(finalDraft);
    localStorage.setItem(LEAD_KEY, JSON.stringify(finalDraft));
    track("chat_lead_captured", {
      name: finalDraft.name || "",
      email: finalDraft.email || "",
      mobile: finalDraft.mobile || finalDraft.phone || "",
      has_name: !!finalDraft.name,
      has_email: !!finalDraft.email,
    });
    try {
      await axios.post(`${API}/leads/chat`, {
        session_id: sessionRef.current,
        ...finalDraft,
        source: "chatbot",
      });
    } catch {
      // Non-blocking — the conversation continues even if the save fails
    }
  }, []);

  const askLlm = useCallback(async (text) => {
    setMsgs((m) => [...m, { role: "assistant", text: "" }]);
    setBusy(true);
    try {
      let acc = "";
      for await (const chunk of streamChat({
        sessionId: sessionRef.current,
        message: text,
        page: pathname,
      })) {
        acc += chunk;
        setMsgs((m) => {
          const cp = [...m];
          cp[cp.length - 1] = { role: "assistant", text: acc };
          return cp;
        });
      }
      if (!acc) {
        setMsgs((m) => {
          const cp = [...m];
          cp[cp.length - 1] = {
            role: "assistant",
            text: "I'm having trouble reaching my thoughts. WhatsApp us at wa.me/918446448123.",
          };
          return cp;
        });
      }
    } catch {
      setMsgs((m) => {
        const cp = [...m];
        cp[cp.length - 1] = {
          role: "assistant",
          text: "Something rustled the leaves. Try again in a moment.",
        };
        return cp;
      });
    } finally {
      setBusy(false);
    }
  }, [pathname]);

  const submit = useCallback(
    async (raw) => {
      const text = raw.trim();
      if (!text || busy) return;
      setInput("");
      track("chat_message", { path: pathname, len: text.length });
      setMsgs((m) => [...m, { role: "user", text }]);

      // If we're still gating for the lead
      if (gateStep >= 0 && gateStep < GATE_STEPS.length) {
        const step = GATE_STEPS[gateStep];
        if (!step.validate(text)) {
          setMsgs((m) => [...m, { role: "assistant", text: step.error }]);
          return;
        }
        const nextDraft = { ...gateDraft, [step.key]: text.trim() };
        setGateDraft(nextDraft);

        if (gateStep < GATE_STEPS.length - 1) {
          const nextIdx = gateStep + 1;
          setGateStep(nextIdx);
          const nextPrompt = GATE_STEPS[nextIdx].prompt.replace("{name}", nextDraft.name || "there");
          setMsgs((m) => [...m, { role: "assistant", text: nextPrompt }]);
        } else {
          // Lead complete — persist, then answer any pending question or a graceful segue
          setGateStep(-1);
          await persistLead(nextDraft);
          const thanks = `Thanks, ${nextDraft.name.split(" ")[0]}. Ask me anything now — and if you'd like, just say "send on WhatsApp" and I'll ping the details to +91 ${nextDraft.mobile}.`;
          setMsgs((m) => [...m, { role: "assistant", text: thanks }]);
          if (pendingUserQ) {
            setMsgs((m) => [...m, { role: "user", text: pendingUserQ }]);
            await askLlm(pendingUserQ);
            setPendingUserQ(null);
          }
        }
        return;
      }

      // Lead already captured OR just captured — ask the LLM
      if (!lead && gateStep >= 0) {
        // Shouldn't happen because we handled above, but safety
        return;
      }
      await askLlm(text);
    },
    [busy, gateStep, gateDraft, lead, pendingUserQ, persistLead, askLlm]
  );

  // If the visitor tries to ask a real question before the gate finishes,
  // stash it and answer after they complete the gate.
  const onSend = (rawText) => {
    if (gateStep >= 0 && !pendingUserQ) {
      // First message from user might be a real question if they're impatient.
      // We only stash on the FIRST step so we don't lose the intent.
      if (gateStep === 0) setPendingUserQ(rawText);
    }
    submit(rawText);
  };

  const gateInputType = gateStep >= 0
    ? { name: "text", mobile: "tel", email: "email", city: "text" }[GATE_STEPS[gateStep].key]
    : "text";
  const gatePlaceholder = gateStep >= 0
    ? { name: "First name", mobile: "10-digit mobile", email: "you@example.com", city: "Mumbai / Pune / …" }[GATE_STEPS[gateStep].key]
    : "Ask about plots, RERA, amenities, the launch…";

  return (
    <>
      {!open && (
        <button
          data-testid={TID.mascotLauncher}
          onClick={() => { track("chat_open", { via: "launcher" }); setOpen(true); }}
          className="mascot-launcher group"
          aria-label="Chat with Peat"
        >
          <div className="relative">
            <div className="w-16 h-16 rounded-full grid place-items-center bg-white shadow-[0_20px_40px_-16px_rgba(11,74,61,0.55)] ring-1 ring-forest/10 mascot-bob">
              <OwlMascot size={44} />
            </div>
            <div className="absolute -top-2 -right-1 w-4 h-4 rounded-full bg-lime border-2 border-white" />
          </div>
        </button>
      )}

      {open && (
        <div
          className="fixed z-[60] bottom-44 md:bottom-28 right-4 md:right-8 w-[calc(100vw-2rem)] sm:w-[380px] max-h-[75vh] bg-white rounded-2xl shadow-[0_40px_80px_-40px_rgba(11,74,61,0.5)] border border-forest/10 overflow-hidden flex flex-col"
          role="dialog"
          aria-label="Chat with Peat"
        >
          <div className="px-4 py-3 flex items-center gap-3 bg-forest text-cream">
            <OwlMascot size={30} />
            <div className="flex-1">
              <div className="font-display text-base leading-none">Peat</div>
              <div className="text-[11px] opacity-70">
                {lead ? `Chatting with ${lead.name.split(" ")[0]}` : "The forest-owl · Pali Hill City"}
              </div>
            </div>
            <button
              data-testid={TID.mascotClose}
              onClick={() => setOpen(false)}
              aria-label="Close chat"
              className="text-cream/80 hover:text-lime transition-colors"
            >
              <X size={18} />
            </button>
          </div>

          <div
            data-testid={TID.mascotMessages}
            ref={scrollerRef}
            className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-offwhite"
          >
            {msgs.map((m, i) => (
              <div
                key={i}
                className={`text-[14px] leading-relaxed max-w-[85%] whitespace-pre-line ${
                  m.role === "user"
                    ? "ml-auto bg-forest text-cream rounded-2xl rounded-br-md px-3.5 py-2.5"
                    : "bg-white text-forest border border-forest/10 rounded-2xl rounded-bl-md px-3.5 py-2.5"
                }`}
              >
                {m.text || <span className="opacity-50">…</span>}
              </div>
            ))}
            {gateStep >= 0 && (
              <div className="text-[10px] text-forest/40 text-center pt-1 tracking-widest uppercase">
                Step {gateStep + 1} of {GATE_STEPS.length}
              </div>
            )}
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              onSend(input);
            }}
            className="border-t border-forest/10 p-3 flex items-center gap-2 bg-white"
          >
            <input
              data-testid={TID.mascotInput}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={gatePlaceholder}
              type={gateInputType}
              className="flex-1 px-3 py-2 text-sm bg-transparent focus:outline-none placeholder:text-forest/40 text-forest"
              disabled={busy}
            />
            <button
              data-testid={TID.mascotSend}
              className="w-9 h-9 rounded-full grid place-items-center bg-lime text-void disabled:opacity-40"
              type="submit"
              disabled={busy || !input.trim()}
              aria-label="Send"
            >
              <Send size={15} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
